/**
 * Market configuration — single source of truth for currency, pricing model,
 * copy variations and VSL asset URLs across all landing-page variants.
 *
 * PRICING MODEL (from Feb 2026):
 *   Booking fee = max(minimum, face_value × percent), charged to the buyer.
 *   Venue always receives 100% of the face value. We absorb Stripe card fees
 *   out of the booking fee.
 */

// Card-processing rate range used to compute "What We Make" / "What We Share
// With You" breakdowns on the venue-facing event form. Applied to the total
// the buyer pays (face_value + booking_fee).
export const CARD_FEE_LOW_PCT = 0.015;    // 1.5% (UK domestic debit/credit)
export const CARD_FEE_HIGH_PCT = 0.025;   // 2.5% (Amex / international)
// Fixed per-transaction card processing fee. UK uses 20p, US uses 20¢ — both
// numerically 0.20 in their own major unit so a single constant is fine.
export const CARD_FEE_FIXED_MAJOR = 0.20;

export const MARKETS = {
  uk: {
    key: "uk",
    label: "United Kingdom",
    currency: "GBP",
    symbol: "£",
    // Pricing model: 5% of face value, minimum £1.25, no cap.
    bookingFeePercent: 0.05,
    bookingFeeMinimum: 1.25,
    // Illustrative savings maths (mid-size event: 1,000 × £15)
    savings: {
      ticketPrice: 15,
      ticketCount: 1000,
      revenue: 15000,
      competitorPlatformPct: 0.10,
      competitorCardLabel: "1.5% + 20p",
      competitorCardTotal: 425,
      competitorNet: 13075,
      ourNet: 15000,
      delta: 1925,
    },
    vslUrl: "https://player.vimeo.com/video/1215569552?h=357ff6ded7&badge=0&autopause=0&player_id=0&app_id=58479&controls=0&title=0&byline=0&portrait=0&pip=0",
  },

  us: {
    key: "us",
    label: "United States",
    currency: "USD",
    symbol: "$",
    // Pricing model: 6% of face value, minimum $1.25, no cap.
    bookingFeePercent: 0.06,
    bookingFeeMinimum: 1.25,
    savings: {
      ticketPrice: 30,
      ticketCount: 1000,
      revenue: 30000,
      competitorPlatformPct: 0.10,
      competitorCardLabel: "2.9% + 30¢",
      competitorCardTotal: 1170,
      competitorNet: 25830,
      ourNet: 30000,
      delta: 4170,
    },
    vslUrl: "",
  },
};

/** The "standard" no-VSL landing defaults to the UK config. */
export const STANDARD_MARKET = "uk";

/** Get a market by key with sensible fallback. */
export function getMarket(key) {
  return MARKETS[key] || MARKETS[STANDARD_MARKET];
}

/**
 * Format a currency amount with the market's symbol and locale.
 * For whole numbers we drop the ".00" for cleaner marketing copy.
 */
export function fmt(market, amount, { decimals } = {}) {
  const n = Number(amount) || 0;
  const dp = decimals ?? (Number.isInteger(n) ? 0 : 2);
  const formatted = n.toLocaleString(market.currency === "GBP" ? "en-GB" : "en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  });
  return `${market.symbol}${formatted}`;
}

/** Booking fee a buyer pays for a ticket at `ticketPrice` (major units). */
export function bookingFeeFor(market, ticketPrice) {
  const pct = (ticketPrice || 0) * (market.bookingFeePercent || 0);
  return Math.max(market.bookingFeeMinimum || 0, Math.round(pct * 100) / 100);
}

/**
 * Estimate Stripe card processing on a `buyerTotal` (face value + booking fee).
 * Returns {low, high} in major units. Applies to both markets — the endpoints
 * mirror standard Stripe rates in each region.
 */
export function cardFeeRangeFor(buyerTotal) {
  const t = buyerTotal || 0;
  return {
    low: Math.round((t * CARD_FEE_LOW_PCT + CARD_FEE_FIXED_MAJOR) * 100) / 100,
    high: Math.round((t * CARD_FEE_HIGH_PCT + CARD_FEE_FIXED_MAJOR) * 100) / 100,
  };
}

/**
 * Compute the full per-ticket breakdown shown on the venue-facing event form:
 *   - You get (bank account)         — always 100% face value
 *   - What your customer sees        — face_value + booking_fee
 *   - What we make (range)           — booking_fee - card_fee_range
 *   - What we share with you (range) — card_fee_range we absorb
 *
 * All returned values are numbers in the market's major unit. Consumers format
 * via `fmt(market, value, { decimals: 2 })`.
 */
export function pricingBreakdown(market, ticketPrice) {
  const price = Number(ticketPrice) || 0;
  const bookingFee = bookingFeeFor(market, price);
  const buyerTotal = Math.round((price + bookingFee) * 100) / 100;
  const { low: cardLow, high: cardHigh } = cardFeeRangeFor(buyerTotal);
  return {
    bookingFee,
    buyerTotal,
    venueReceives: price,
    ourNetLow: Math.max(0, Math.round((bookingFee - cardHigh) * 100) / 100),
    ourNetHigh: Math.max(0, Math.round((bookingFee - cardLow) * 100) / 100),
    weCoverLow: cardLow,
    weCoverHigh: cardHigh,
  };
}
